import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, Send, Mail } from 'lucide-react';

export interface IntakeSendEmailReviewModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  onSend: (subject: string, bodyPlain: string) => Promise<void>;
  customerEmail: string;
  customerFirstName: string;
  businessName: string;
  intakeUrl: string;
  formTitle?: string;
}

export default function IntakeSendEmailReviewModal({
  isOpen,
  onDismiss,
  onSend,
  customerEmail,
  customerFirstName,
  businessName,
  intakeUrl,
  formTitle,
}: IntakeSendEmailReviewModalProps) {
  const [subject, setSubject] = useState('');
  const [bodyPlain, setBodyPlain] = useState('');
  const [sending, setSending] = useState(false);

  const first = (customerFirstName || 'there').trim() || 'there';
  const biz = (businessName || 'Our team').trim() || 'Our team';

  useEffect(() => {
    if (!isOpen) {
      setSubject('');
      setBodyPlain('');
      setSending(false);
      return;
    }
    setSubject(`${biz} — please complete your form`);
    setBodyPlain(
      `Hi ${first},\n\nPlease complete this short form:\n\n${intakeUrl}\n\nThank you,\n${biz}`,
    );
  }, [isOpen, first, biz, intakeUrl]);

  const handleSend = async () => {
    if (!subject.trim() || !bodyPlain.trim()) return;
    setSending(true);
    try {
      await onSend(subject.trim(), bodyPlain.trim());
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[20030] flex items-end sm:items-center justify-center sm:p-4">
      <button type="button" className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !sending && onDismiss()} aria-label="Close" />
      <div
        className="relative w-full sm:max-w-lg bg-white sm:rounded-2xl shadow-2xl border border-gray-200 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-9 w-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
              <Mail className="h-4 w-4 text-blue-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-gray-900">Review email</h2>
              <p className="text-xs text-gray-500 truncate">{formTitle || 'Intake form link'}</p>
            </div>
          </div>
          <button
            type="button"
            disabled={sending}
            onClick={onDismiss}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-40"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
            <div className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 truncate">{customerEmail}</div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={sending}
              className="w-full px-3 py-2.5 text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/25 focus:border-blue-500 disabled:opacity-50"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Message</label>
            <textarea
              rows={10}
              value={bodyPlain}
              onChange={(e) => setBodyPlain(e.target.value)}
              disabled={sending}
              className="w-full px-3 py-2.5 text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/25 focus:border-blue-500 resize-none disabled:opacity-50"
              style={{ fontSize: '16px', WebkitTapHighlightColor: 'transparent' }}
            />
            <p className="text-xs text-gray-500 mt-1.5">
              Plain text; line breaks are preserved. The form link is added automatically if you remove it.
            </p>
          </div>
        </div>

        <div className="px-4 py-3 border-t border-gray-100 flex gap-2" style={{ paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))' }}>
          <button
            type="button"
            disabled={sending}
            onClick={onDismiss}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={sending || !subject.trim() || !bodyPlain.trim()}
            onClick={() => void handleSend()}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Send email
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
